<template>
  <div>
    <div class="d-flex align-center">
      <h2>{{ $t('guidance.title') }}</h2>
      <v-spacer />
      <v-btn
        @click="editDialog = true"
        :disabled="loading"
        color="primary"
        variant="flat"
      >
        <v-icon start>mdi-plus</v-icon>
        {{ $t('common.create') }}
      </v-btn>
    </div>

    <v-card variant="outlined" color="grey lighten-5" class="mt-2">
      <v-container>
        <v-row>
          <v-col cols="3">
            <h5>{{ $t('guidance.year') }}</h5>
            <v-text-field
              v-model="searchFilters.year"
              variant="outlined"
              hide-details=""
              density="compact"
              :disabled="loading"
              type="number"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('guidance.month') }}</h5>
            <v-text-field
              v-model="searchFilters.month"
              variant="outlined"
              hide-details=""
              density="compact"
              :disabled="loading"
              type="number"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('guidance.week') }}</h5>
            <v-text-field
              v-model="searchFilters.week"
              variant="outlined"
              hide-details=""
              density="compact"
              :disabled="loading"
              type="number"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <h5>{{ $t('guidance.type') }}</h5>
            <v-select
              v-model="searchFilters.type"
              :items="guidancePlanTypeItems"
              variant="outlined"
              hide-details=""
              density="compact"
              :disabled="loading"
              clearable
            ></v-select>
          </v-col>
        </v-row>
        <v-row align="center" justify="center">
          <v-col cols="2">
            <v-btn
              @click="listPlan"
              :loading="loading"
              color="primary"
              variant="flat"
              block
              class="search-button"
              >{{ $t('common.search') }}</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <div class="mt-6">
      <v-data-table
        :headers="headers"
        :items="planResponse ? planResponse.data : []"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- createdAt -->
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <!-- updatedAt -->
        <template v-slot:[`item.updatedAt`]="{ item }">
          {{ formatDate(item.updatedAt) }}
        </template>

        <!-- detail -->
        <template v-slot:[`item.detail`]="{ item }">
          <v-btn
            :to="`/guidance-plan/detail/${item.id}`"
            :disabled="loading"
            color="primary"
            variant="outlined"
            small
          >
            {{ $t('common.detail') }}
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- pagination -->
    <v-pagination
      class="mt-6"
      v-if="planResponse"
      v-model="page"
      :length="Math.ceil(planResponse.count / searchFilters.limit)"
    ></v-pagination>

    <!-- dialog -->
    <v-dialog v-model="editDialog" max-width="700px">
      <v-card>
        <v-card-title>{{ $t('guidance.title') }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="6"> {{ $t('guidance.name') }}* </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="input.name"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6"> {{ $t('guidance.year') }}* </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="input.year"
                  type="number"
                  :suffix="$t('common.schoolYear')"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6"> {{ $t('guidance.month') }} </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="input.month"
                  type="number"
                  suffix=""
                  variant="outlined"
                  density="compact"
                  hide-details=""
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6"> {{ $t('guidance.week') }} </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="input.week"
                  type="number"
                  suffix=""
                  variant="outlined"
                  density="compact"
                  hide-details=""
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6"> {{ $t('guidance.formTemplate') }}* </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.planFormId"
                  :items="formItems"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6"> {{ $t('guidance.parentPlan') }}* </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.parentPlanId"
                  :items="planItems"
                  no-data-text="No data available"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6"> {{ $t('common.class') }} </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.classId"
                  item-title="className"
                  item-value="classId"
                  :items="classes"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="editDialog = false" color="grey" text>
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                @click="createPlan()"
                :disabled="loading"
                color="primary"
                variant="flat"
              >
                {{ $t('common.create') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

,
<script lang="ts">
import ApiGuidance from "@/api/ApiGuidance";
import { GuidancePlanTypeItems } from "@/constants/guidance";
import LocalDataService from "@/service/LocalDataService";
import { classModule } from "@/store/dataModules/classModule";
import {
  GuidancePlanCategoryType,
  NurseryClassResponse,
  NurseryGuidancePlanFormListResponse,
  NurseryGuidancePlanListResponse,
} from "chaild-api/lib";
import dayjs from "dayjs";
import { defineComponent } from "vue";

export default defineComponent({
  name: "GuidancePlanList",

  async mounted() {
    await this.listPlan();
    await this.listForm();
    await this.listClass();
  },

  data() {
    return {
      loading: false,
      planResponse: null as null | NurseryGuidancePlanListResponse,
      formResponse: null as null | NurseryGuidancePlanFormListResponse,
      headers: [
        { title: this.$t('guidance.name'), key: "name" },
        { title: this.$t('guidance.year'), key: "year" },
        { title: this.$t('guidance.month'), key: "month" },
        { title: this.$t('guidance.week'), key: "week" },
        { title: this.$t('guidance.createdAt'), key: "createdAt" },
        { title: this.$t('guidance.updatedAt'), key: "updatedAt" },
        { title: this.$t('common.detail'), key: "detail" },
      ],
      searchFilters: {
        limit: 10,
        skip: 0,
        type: "" as GuidancePlanCategoryType,
        year: dayjs().year(),
        month: "",
        week: "",
      },
      input: {
        name: "",
        planFormId: "",
        parentPlanId: "",
        year: dayjs().year(),
        month: dayjs().month(),
        week: 1,
        classId: "",
      },
      editDialog: false,
      guidancePlanTypeItems: GuidancePlanTypeItems,
    };
  },

  computed: {
    planItems: function (): any[] {
      if (this.planResponse) {
        return this.planResponse.data.map((p) => {
          return {
            text: p.name,
            value: p.id,
          };
        });
      } else {
        return [];
      }
    },
    formItems: function (): any[] {
      if (this.formResponse) {
        return this.formResponse.data.map((f) => {
          return {
            text: f.name,
            value: f.id,
          };
        });
      } else {
        return [];
      }
    },
    classes: function (): NurseryClassResponse[] {
      return classModule.classes;
    },
    page: {
      get(): number {
        if (this.searchFilters.skip == 0) {
          return 1;
        }
        return Math.ceil(
          this.searchFilters.skip / (this.searchFilters.limit - 1)
        );
      },
      set(value: number) {
        this.searchFilters.skip = (value - 1) * this.searchFilters.limit;
        this.listPlan();
      },
    },
  },

  methods: {
    async listPlan() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.listPlan({
          nurseryId,
          ...this.searchFilters,
        }).catch(() => (this.loading = false));
        if (response) {
          this.planResponse = response;
        }
      }
      this.loading = false;
    },
    async createPlan() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.createPlan({
          nurseryId,
          ...this.input,
        }).catch(() => (this.loading = false));
        if (response) {
          this.editDialog = false;
          await this.listPlan();
        }
      }
      this.loading = false;
    },
    async listForm() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.listPlanForm({
          nurseryId,
          ...this.searchFilters,
        }).catch(() => (this.loading = false));
        if (response) {
          this.formResponse = response;
        }
      }
      this.loading = false;
    },
    async listClass() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule
          .listClass({
            nurseryId,
          })
          .catch();
      }
    },
    clearInput() {
      this.input = {
        name: "",
        planFormId: "",
        parentPlanId: "",
        year: dayjs().year(),
        month: dayjs().month(),
        week: 1,
        classId: "",
      };
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("DD/MM/YYYY");
    },
  },
});
</script>
